import { createLogic } from "redux-logic";
import type {
    FetchExternalPriceFunctionsType,
    SetExternalPriceTaxLocationType,
    StartExternalPriceFetchType,
} from "../../actions/ExternalPriceActions";
import {
    FETCH_EXTERNAL_PRICE_FUNCTIONS,
    finishExternalPriceValueFetch,
    SET_EXTERNAL_PRICE_TAX_LOCATION,
    setExternalPriceCalculatorReady,
    setExternalPriceError,
    setExternalPriceFunctionFetch,
    START_EXTERNAL_PRICE_VALUE_FETCH,
} from "../../actions/ExternalPriceActions";
import { ExternalPriceCalculator } from "../../../utils/classes/ExternalPriceCalculator";
import Debug from "../../../../../../common-deprecated/Debug";
import { getConfigSpecsBody } from "../../../utils/techSpecUtils";
import { calculatePrice } from "../../actions/BasketActions";
import type { ConfigSpecsRequestObjectType } from "../../../../../shared-logic/types/ConfigurationSpecificationsObjectType";
import type { BuildAndBuyLogicType } from "../../store";
import { finishMassManagementFetch, startMassManagementFetch } from "../../actions/MassManagementActions";
import { validateMassManagementResult } from "../MassManagementLogic";
import { getAPI } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { getSessionStorageParams, setTaxLocationIdToStorage } from "../../../utils/sessionStorage";
import { startConfigSave } from "../../actions/SaveActions";
import { loadMassManagement } from "../../../utils/settings/bnbSettingUtils";
import { addTokenDebugVariablesToUrl } from "../../../../../shared-logic/utils/typedUtils";

const startExternalPriceFetchLogic = (createLogic as BuildAndBuyLogicType<StartExternalPriceFetchType>)({
    type: START_EXTERNAL_PRICE_VALUE_FETCH,
    async process({ getState, action }, dispatch, done) {
        const { basket, car, items, commonSettings, bnbSettings } = getState();

        const loadMassMngmnt = loadMassManagement(bnbSettings);
        try {
            if (loadMassMngmnt) dispatch(startMassManagementFetch(false));
            const body = getConfigSpecsBody(getState(), true);
            const request = await fetch(
                addTokenDebugVariablesToUrl(commonSettings, getAPI(commonSettings, "configurationspecifications")),
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                },
            );

            const result: ConfigSpecsRequestObjectType = await request.json();

            if (loadMassMngmnt) validateMassManagementResult(result, dispatch);

            const externalTotalPrice = ExternalPriceCalculator.calculatePrice(
                result.specificationMapping,
                car,
                basket,
                items,
                commonSettings,
            );

            if (!externalTotalPrice) throw new Error("Calculate function not defined");
            if (Number.isNaN(externalTotalPrice!.totalValue)) throw new Error("Calculate function returned NaN");

            dispatch(finishExternalPriceValueFetch(externalTotalPrice, result.co2Specs || null));
        } catch (e) {
            Debug.error("Failed calculating external price:", (e as Error).toString());
            // Set error state so the calculate logic knows that external price has been executed with an error.
            // After price calculations have been done the error state will be reset, as errors could occur for specific configurations.
            dispatch(setExternalPriceError(true));
            if (loadMassMngmnt) dispatch(finishMassManagementFetch(0, true));
        }

        // Don't trigger a reload of the external price once finished
        dispatch(calculatePrice(false, action.firstTimeLoad));
        done();
    },
});

const prepareExternalPriceLogic = (createLogic as BuildAndBuyLogicType<FetchExternalPriceFunctionsType>)({
    type: FETCH_EXTERNAL_PRICE_FUNCTIONS,

    latest: true, // Added for duplicate callings of this function
    async process({ getState }, dispatch, done) {
        const { bnbSettings } = getState();
        try {
            // Fetch external price js functions and initialize.
            const result = await fetch(bnbSettings.priceCalculationService);
            const parsedResult = await result.json();
            const success = ExternalPriceCalculator.initialize(parsedResult.result);
            dispatch(setExternalPriceCalculatorReady(success));
        } catch (e) {
            Debug.error("Failed preparing external price calculator", (e as Error).toString());
            // External price calculator failed so set ready as false. This will prevent OR from trying to calculate external prices.
            dispatch(setExternalPriceCalculatorReady(false));
        }

        dispatch(setExternalPriceFunctionFetch(false));
        // This logic is triggered only once during initialisation of the application
        // therefor we use the firstTime = true boolean flag to initialize the quote config (updateQuoteConfig)
        dispatch(calculatePrice(true, true));

        done();
    },
});

const setTaxLocationExternalPriceLogic = (createLogic as BuildAndBuyLogicType<SetExternalPriceTaxLocationType>)({
    type: SET_EXTERNAL_PRICE_TAX_LOCATION,

    process({ action, getState }, dispatch, done) {
        const { commonSettings } = getState();
        const sessionStorageParams = getSessionStorageParams(commonSettings);

        setTaxLocationIdToStorage(sessionStorageParams, action.locationId || "");

        if (action.triggerCalculation) {
            // Calculate price with the new taxLocation
            dispatch(calculatePrice(true, true));
            // Save the config again as price calculation has changed
            dispatch(startConfigSave());
        }

        done();
    },
});

export default [startExternalPriceFetchLogic, prepareExternalPriceLogic, setTaxLocationExternalPriceLogic];
